import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Save } from "lucide-react";

export default function AdminSettings() {
    const [s, setS] = useState(null);
    useEffect(() => { api.get("/admin/settings").then((r) => setS(r.data)); }, []);

    const save = async () => {
        try {
            const payload = { ...s };
            delete payload.id;
            delete payload._id;
            await api.patch("/admin/settings", payload);
            toast.success("Settings saved");
        } catch (e) { toast.error("Save failed"); }
    };

    if (!s) return <div className="min-h-[40vh] flex items-center justify-center font-mono text-muted-foreground">Loading…</div>;

    const F = (k, label, opts = {}) => (
        <div>
            <Label className="overline">{label}</Label>
            <Input data-testid={`settings-${k}-input`} type={opts.type || "text"} value={s[k] ?? ""} onChange={(e) => setS({ ...s, [k]: opts.type === "number" ? Number(e.target.value) : e.target.value })}
                className="rounded-sm mt-2 font-mono" />
        </div>
    );

    return (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
            <span className="overline text-primary">▮ Platform settings</span>
            <h1 className="mt-2 font-heading font-black text-4xl tracking-tighter">Configure the platform</h1>

            <div className="mt-8 grid md:grid-cols-2 gap-4">
                <div className="border border-border rounded-sm p-6">
                    <h3 className="font-heading font-bold text-lg">Free tier limits</h3>
                    <div className="mt-4 space-y-4">
                        {F("free_generation_limit", "Free generations", { type: "number" })}
                        {F("free_ai_analytics_limit", "Free AI analytics", { type: "number" })}
                    </div>
                </div>

                <div className="border border-border rounded-sm p-6">
                    <h3 className="font-heading font-bold text-lg">Subscription pricing</h3>
                    <div className="mt-4 space-y-4">
                        {F("subscription_price_monthly", "Monthly price", { type: "number" })}
                        {F("subscription_price_yearly", "Yearly price", { type: "number" })}
                        {F("currency", "Currency (INR/USD)")}
                    </div>
                </div>

                <div className="border border-border rounded-sm p-6">
                    <h3 className="font-heading font-bold text-lg">Exam defaults</h3>
                    <div className="mt-4 space-y-4">
                        {F("default_question_count", "Default question count", { type: "number", min: 5, max: 50 })}
                        {F("default_exam_duration_minutes", "Default duration (min)", { type: "number" })}
                    </div>
                </div>

                <div className="border border-border rounded-sm p-6">
                    <h3 className="font-heading font-bold text-lg">Scoring rules</h3>
                    <div className="mt-4 grid grid-cols-2 gap-3">
                        {F("marks_per_correct_neet", "NEET +", { type: "number" })}
                        {F("marks_per_incorrect_neet", "NEET −", { type: "number" })}
                        {F("marks_per_correct_jee", "JEE +", { type: "number" })}
                        {F("marks_per_incorrect_jee", "JEE −", { type: "number" })}
                    </div>
                </div>

                <div className="md:col-span-2 border border-border rounded-sm p-6">
                    <h3 className="font-heading font-bold text-lg">Landing page content</h3>
                    <div className="mt-4 space-y-4">
                        {F("hero_headline", "Hero headline")}
                        <div>
                            <Label className="overline">Hero subtext</Label>
                            <Textarea data-testid="settings-hero_subtext-input" value={s.hero_subtext} onChange={(e) => setS({ ...s, hero_subtext: e.target.value })}
                                className="rounded-sm mt-2 font-mono min-h-[6rem]" />
                        </div>
                    </div>
                </div>
            </div>

            <Button data-testid="settings-save-btn" onClick={save} className="mt-6 rounded-sm bg-primary hover:bg-primary/90 btn-press">
                <Save className="w-4 h-4 mr-2" /> Save settings
            </Button>
        </div>
    );
}
